/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useServerData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading Telephony Server data for edit/duplicate mode.
────────────────────────────────────────────────────────────────*/

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchTelephonyServerById } from '../api';
import { TelephonyServerFormData } from '../types';

export function useServerData(
  mode: 'create' | 'edit',
  id: string | undefined,
  duplicateId: string | null,
  setFormData: (data: TelephonyServerFormData | ((prev: TelephonyServerFormData) => TelephonyServerFormData)) => void,
  setOriginalData: (data: TelephonyServerFormData) => void,
  resetParam?: string | null,
) {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || '';
  const serverLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);
  
  // Use refs for functions to avoid re-renders
  const setFormDataRef = useRef(setFormData);
  const setOriginalDataRef = useRef(setOriginalData);
  const showErrorRef = useRef(showError);
  
  // Update refs when functions change
  useEffect(() => {
    setFormDataRef.current = setFormData;
    setOriginalDataRef.current = setOriginalData;
    showErrorRef.current = showError;
  }, [setFormData, setOriginalData, showError]);

  useEffect(() => {
    const loadServer = async () => {
      const serverId = duplicateId || id;
      if (!serverId || serverId === 'new') return;

      try {
        const serverData = await fetchTelephonyServerById(serverId, tenantSlug, user?.id);

        const loadedData: TelephonyServerFormData = {
          name: duplicateId ? `${serverData.name} ${t('telephonyServers.copySuffix')}` : serverData.name,
          description:
            duplicateId && serverData.description
              ? `${serverData.description} ${t('telephonyServers.copySuffix')}`
              : serverData.description || '',
          host: serverData.host || '',
          port: serverData.port || 22,
          username: serverData.username || '',
          password: '', // Don't load passwords
          eslHost: serverData.eslHost || serverData.host || '',
          freeswitchPort: serverData.freeswitchPort || 8021,
          freeswitchPassword: '', // Don't load passwords
          timezone: serverData.timezone || 'UTC',
          location: serverData.location || '',
          monitoringEnabled: serverData.monitoringEnabled ?? true,
          healthCheckInterval: serverData.healthCheckInterval || 30,
          useVpn: serverData.useVpn ?? false,
          bindInterface: serverData.bindInterface || 'auto',
          vpnIp: serverData.vpnIp || '',
        };

        if (!duplicateId && serverData.id) {
          (loadedData as any).id = serverData.id;
        }

        setFormDataRef.current(loadedData);
        setOriginalDataRef.current(loadedData);
      } catch (error) {
        showErrorRef.current(error instanceof Error ? error.message : t('telephonyServers.failedToLoad'));
      }
    };

    const currentKey = `${mode}-${id || 'new'}-${duplicateId || 'none'}`;
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
    const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;
    
    // If reset param is present and not yet processed, force reset
    if (hasResetParam) {
      formResetRef.current = false;
      serverLoadedRef.current = null;
      // Clear the lastIdRef to ensure we detect the change
      lastIdRef.current = undefined;
      // Mark this reset param as processed
      resetParamProcessedRef.current = resetParam;
    }
    
    if (mode === 'edit' || duplicateId) {
      if (serverLoadedRef.current === currentKey && !hasResetParam && !idChangedToNew) {
        lastIdRef.current = currentId;
        return;
      }
      
      // Reset the form reset flag when loading an actual server
      formResetRef.current = false;
      loadServer();
      serverLoadedRef.current = currentKey;
      lastIdRef.current = currentId;
    } else if (mode === 'create') {
      // Reset form when navigating to new (either initial load or after save)
      if (hasResetParam || !formResetRef.current || idChangedToNew || serverLoadedRef.current === null) {
        formResetRef.current = true;
        lastIdRef.current = currentId;
        serverLoadedRef.current = 'create-initialized';
        const initialState: TelephonyServerFormData = {
          name: '',
          description: '',
          host: '',
          port: 22,
          username: '',
          password: '',
          eslHost: '',
          freeswitchPort: 8021,
          freeswitchPassword: 'ClueCon',
          timezone: 'UTC',
          location: '',
          monitoringEnabled: true,
          healthCheckInterval: 30,
        };
        setFormDataRef.current(initialState);
        setOriginalDataRef.current(initialState);
      } else {
        lastIdRef.current = currentId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, duplicateId, tenantSlug, user?.id, resetParam]);

  // Auto-populate eslHost when host changes (in create mode)
  useEffect(() => {
    if (mode === 'create') {
      setFormData((prev) => {
        if (!prev.eslHost && prev.host) {
          return {
            ...prev,
            eslHost: prev.host,
          };
        }
        return prev;
      });
    }
  }, [mode, setFormData]);
}

